import axios from 'axios';

/**
 * Interfejs dla ulicy z TERYT API
 */
export interface TerytStreet {
  symbol: string;
  nameFull: string;
}

/**
 * Interfejs dla ulicy z override (zawiera dodatkowe pola konfiguracyjne)
 */
export interface OverrideStreet extends TerytStreet {
  terc: string;
  applyTo: string;
}

/**
 * Typ unijny dla ulicy (może być z API lub override)
 */
export type Street = TerytStreet | OverrideStreet;

/**
 * Interfejs dla odpowiedzi API TERYT
 */
interface TerytApiResponse {
  list: TerytStreet[];
}

/**
 * Override dla ulic z Przewłoki, które nie są zwracane przez TERYT API
 * Symbol: 90xxx to tymczasowy zakres (VARCHAR(5) w bazie danych)
 * Gdy TERYT API zacznie zwracać te ulice, należy usunąć wpisy i zaktualizować symbole w bazie
 */
export const ULIC_OVERRIDE: OverrideStreet[] = [];

/**
 * Filtruje ulice override dla danego TERC
 * @param terc - Kod TERC gminy
 * @returns Tablica ulic z override pasujących do TERC
 */
export const getOverrideStreetsForTerc = (terc: string): OverrideStreet[] => {
  return ULIC_OVERRIDE.filter(street => street.applyTo === terc);
};

/**
 * Filtruje ulice override dla danego TERC i frazy wyszukiwania
 * @param terc - Kod TERC gminy
 * @param searchText - Fraza wyszukiwania
 * @returns Tablica ulic z override pasujących do TERC i frazy
 */
export const searchOverrideStreets = (terc: string, searchText: string): OverrideStreet[] => {
  const searchLower = searchText.toLowerCase();
  return ULIC_OVERRIDE.filter(street => street.applyTo === terc && street.nameFull.toLowerCase().includes(searchLower));
};

/**
 * Znajduje ulicę w override po symbolu
 * @param symbol - Symbol ulicy
 * @returns Znaleziona ulica lub undefined
 */
export const findOverrideStreetBySymbol = (symbol: string): OverrideStreet | undefined => {
  return ULIC_OVERRIDE.find(street => street.symbol === symbol);
};

/**
 * Łączy ulice z API i override
 * @param apiStreets - Ulice z TERYT API
 * @param overrideStreets - Ulice z override
 * @returns Połączona tablica ulic
 */
export const mergeStreets = (apiStreets: Street[], overrideStreets: Street[]): Street[] => {
  return [...apiStreets, ...overrideStreets];
};

/**
 * Sprawdza czy są dostępne ulice (API lub override)
 * @param apiCount - Liczba ulic z API
 * @param terc - Kod TERC gminy
 * @returns true jeśli są jakiekolwiek ulice
 */
export const hasAnyStreets = (apiCount: number, terc: string): boolean => {
  const overrideCount = getOverrideStreetsForTerc(terc).length;
  return apiCount > 0 || overrideCount > 0;
};

/**
 * Sprawdza czy lista ulic nie jest pusta (zapytanie count do API)
 * @param terc - Kod TERC gminy
 * @param simc - Symbol miejscowości
 * @returns Promise zwracający true jeśli są ulice
 */
export const checkIfStreetsExist = async (terc: string, simc: string): Promise<boolean> => {
  if (!terc || !simc) {
    return false;
  }

  try {
    const response = await axios.get<TerytApiResponse>(
      `${process.env.VUE_APP_TERYT_API_URL}/ulic/?terc=${terc}&placeSymbol=${simc}&count=1`
    );
    return hasAnyStreets(response.data.list.length, terc);
  } catch (error) {
    console.error('Błąd podczas sprawdzania listy ulic:', error);
    return false;
  }
};

/**
 * Wyszukuje ulice w TERYT API i merguje z override
 * @param terc - Kod TERC gminy
 * @param simc - Symbol miejscowości
 * @param searchText - Fraza wyszukiwania
 * @returns Promise zwracający tablicę ulic
 */
export const searchStreets = async (terc: string, simc: string, searchText: string): Promise<Street[]> => {
  if (!terc || !simc) {
    return [];
  }

  try {
    const response = await axios.get<TerytApiResponse>(
      `${process.env.VUE_APP_TERYT_API_URL}/ulic/?terc=${terc}&placeSymbol=${simc}&search=${searchText}`
    );
    const apiStreets = response.data.list;
    const overrideStreets = searchOverrideStreets(terc, searchText);
    return mergeStreets(apiStreets, overrideStreets);
  } catch (error) {
    console.error('Błąd podczas wyszukiwania ulic:', error);
    throw error;
  }
};

/**
 * Pobiera nazwę ulicy na podstawie symbolu (najpierw override, potem API)
 * @param simc - Symbol miejscowości
 * @param symbol - Symbol ulicy
 * @returns Promise zwracający nazwę ulicy lub null
 */
export const getStreetNameBySymbol = async (simc: string, symbol: string): Promise<string | null> => {
  const overrideStreet = findOverrideStreetBySymbol(symbol);
  if (overrideStreet) {
    return overrideStreet.nameFull;
  }

  if (!simc) {
    return null;
  }

  try {
    const response = await axios.get<TerytStreet>(`${process.env.VUE_APP_TERYT_API_URL}/ulic/${simc}/${symbol}`);
    return response.data.nameFull;
  } catch (error) {
    console.error('Błąd podczas pobierania nazwy ulicy:', error);
    throw error;
  }
};
